import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useMap } from "react-map-gl"; // Import Mapbox components
import { delete_polygon, search_map, stree_view, protractor, measure_dims } from "../../../assets/images";
import { inputsActions, usePrevious } from "../../Redux";
import { Popconfirm, Tooltip } from "antd";
import { ToolbarWrap } from "./styles";
import { DrawPolygon, SearchLocations, PolygonTools, MapboxExportMenu } from "./";
import { MapboxLayerMenu } from "./MapboxLayerMenu";

const MapboxToolbar = () => {
  const { current: mapRef } = useMap();
  const map = mapRef.getMap();
  const dispatch = useDispatch();

  // Store
  const selectedFeatureId = useSelector((state) => state.inputs.selectedFeatureId);
  const searchVisible = useSelector((state) => state.inputs.uiState.search_visible);
  const isDrawing = useSelector((state) => state.inputs.uiState.is_drawing);
  const isDrawingPolyline = useSelector((state) => state.inputs.uiState.is_drawing_polyline);
  const generated = useSelector((state) => state.sift.ioManager.outputs.runState.generated);
  const prevSelectedId = usePrevious(selectedFeatureId);
  const additional_layers = useSelector((state) => state.inputs.uiState.additional_layers);

  const show_azi = useSelector((state) => state.inputs.uiState.show_azi);
  const show_dims = useSelector((state) => state.inputs.uiState.show_dims);
  // Use ref to keep track of additional_layers state
  const additional_layers_ref = useRef(additional_layers);

  useEffect(() => {
    additional_layers_ref.current = additional_layers; // Keep ref updated with latest state
  }, [additional_layers]);

  // State
  const [expand, setExpand] = useState(false);
  const [expandMapLayers, setExpandMapLayers] = useState(false);
  const [expandPolygonTools, setExpandPolygonTools] = useState(false);
  const [expandExportTools, setExpandExportTools] = useState(false);
  const [expandHeight, setExpandHeight] = useState(40); // Initialize with a default height

  // Refs
  const expandMapLayersRef = useRef(expandMapLayers);
  const expandExportToolsRef = useRef(expandExportTools);

  // Update refs when state changes
  useEffect(() => {
    expandMapLayersRef.current = expandMapLayers;
  }, [expandMapLayers]);

  useEffect(() => {
    expandExportToolsRef.current = expandExportTools;
  }, [expandExportTools]);

  // Close expanded menus when the map is clicked
  useEffect(() => {
    if (map) {
      const handleClick = () => {
        setExpandMapLayers(false);
        setExpandExportTools(false);
        if (searchVisible) {
          handleToggleSearch();
        }
      };
      map.on("click", handleClick);
      return () => map.off("click", handleClick);
    }
  }, [map, searchVisible]);

  useEffect(() => {
    if (selectedFeatureId) {
      handleTogglePolygonTools(true);
    } else {
      handleTogglePolygonTools(false);
    }
  }, [selectedFeatureId]);

  // Handle toggling functions
  const toggleExpandState = useCallback((setExpandFn, stateValue) => {
    const newHeight = stateValue ? 40 : 435; // Set height based on expanded state
    setExpandFn(!stateValue);
    setExpandHeight(newHeight); // Ensure expandHeight is updated correctly
  }, []);

  const handleToggleSearch = useCallback(() => {
    dispatch(inputsActions.update_ui_state("search_visible", !searchVisible));
    if (!searchVisible) resetExpandedStates();
  }, [searchVisible]);

  const handleToggleMapLayers = useCallback(() => {
    toggleExpandState(setExpandMapLayers, expandMapLayers);
    if (expandMapLayers) resetExpandedStates();
    if (searchVisible) dispatch(inputsActions.update_ui_state("search_visible", false));
    if (!expandPolygonTools) setExpandPolygonTools(false);
  }, [expandMapLayers, searchVisible]);

  const handleTogglePolygonTools = useCallback((toggled) => {
    setExpandPolygonTools(toggled);
    setExpandHeight(40);
    if (!toggled) resetExpandedStates();
  }, []);

  const handleToggleExportTools = useCallback(() => {
    toggleExpandState(setExpandExportTools, expandExportTools);
    resetExpandedStates();
  }, [expandExportTools]);

  const resetExpandedStates = useCallback(() => {
    setExpandMapLayers(false);
    setExpandPolygonTools(false);
    setExpandExportTools(false);
    setExpandHeight(40);
    if (isDrawing) map.fire("cancelDraw");
    if (isDrawingPolyline) dispatch(inputsActions.update_ui_state("is_drawing_polyline", false));
  }, [isDrawing, isDrawingPolyline]);

  // Update Mapbox based on selected feature
  useEffect(() => {
    if (selectedFeatureId) {
      handleTogglePolygonTools(true);
      if (searchVisible) dispatch(inputsActions.update_ui_state("search_visible", false));
    } else {
      handleTogglePolygonTools(false);
    }
    if (prevSelectedId !== selectedFeatureId) setExpandHeight(40);
  }, [selectedFeatureId, prevSelectedId]);

  // Update toolbar based on polyline drawing state
  useEffect(() => {
    if (isDrawingPolyline) {
      resetExpandedStates();
    }
  }, [isDrawingPolyline]);

  const deleteAll = () => {
    map.fire("deleteAllFeatures");
  };

  const handleToggleDims = () => {
    let checked = !show_dims;
    dispatch(inputsActions.update_ui_state("show_dims", checked));
    // if (show_azi) dispatch(inputsActions.update_ui_state("show_azi", false));
  };

  const handleToggleAzi = () => {
    let checked = !show_azi;
    dispatch(inputsActions.update_ui_state("show_azi", checked));
    // if (show_dims) dispatch(inputsActions.update_ui_state("show_dims", false));
  };

  return (
    <ToolbarWrap expandHeight={expandHeight} expand={searchVisible || expandMapLayers}>
      <section className="map-action-buttons">
        <Tooltip placement="left" title="Search" mouseEnterDelay={0.5}>
          <button className={searchVisible ? "button-active" : ""} onClick={handleToggleSearch}>
            <img draggable={false} src={search_map} />
          </button>
        </Tooltip>
        <Tooltip placement="bottom" title="Change Basemap" mouseEnterDelay={0.5}>
          <button className={expandMapLayers ? "button-active" : ""} onClick={handleToggleMapLayers}>
            <img draggable={false} src={stree_view} />
          </button>
        </Tooltip>
        <DrawPolygon />
        <Tooltip placement="bottom" title="Show Polygon Dimensions" mouseEnterDelay={0.25}>
          <button className={show_dims ? "button-active" : ""} style={{ padding: 0 }} disabled={false} onClick={() => handleToggleDims()}>
            {/* <img draggable={false} src={measure_dims} /> */}
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M5.89561 7.66613C6.31905 7.86532 6.8167 7.97739 7.351 7.97789C8.0637 7.97646 8.70998 7.78008 9.20389 7.43699C9.45069 7.26407 9.6617 7.05471 9.82366 6.78863C9.98413 6.52629 10.0999 6.1822 10.0996 5.794C10.0999 5.40573 9.98413 5.06127 9.82366 4.79931C9.58002 4.4023 9.22928 4.1238 8.80616 3.92181C8.38299 3.72262 7.88538 3.61011 7.35104 3.61011C6.63838 3.61098 5.99206 3.80742 5.49788 4.15101C5.25108 4.32393 5.04006 4.53322 4.87815 4.79931C4.71768 5.06127 4.6019 5.40573 4.60217 5.794C4.6019 6.1822 4.71764 6.52623 4.87815 6.78863C5.1217 7.1852 5.47272 7.46413 5.89561 7.66613ZM5.30272 5.4564C5.43174 5.23831 5.70307 4.99438 6.06303 4.82745C6.42268 4.65772 6.86928 4.554 7.351 4.5545C7.99295 4.55307 8.57319 4.73984 8.96717 5.01653C9.16405 5.153 9.31272 5.31257 9.39874 5.45647C9.48647 5.60355 9.50811 5.70919 9.50842 5.79406C9.50811 5.87843 9.48647 5.98451 9.39874 6.1316C9.26999 6.34926 8.99866 6.59362 8.6387 6.76055C8.27905 6.93028 7.83276 7.03356 7.351 7.03356C6.70877 7.03443 6.12858 6.84816 5.73456 6.57153C5.53768 6.435 5.38901 6.2755 5.30276 6.1316C5.21502 5.98451 5.19362 5.87843 5.19334 5.79406C5.19358 5.70913 5.21499 5.60349 5.30272 5.4564Z"
                fill="#333333"
              />
              <path d="M11.2577 0.83667C11.2594 0.837666 11.2614 0.838596 11.2632 0.839592L11.286 0.850883L11.2577 0.83667Z" fill="#333333" />
              <path d="M3.44424 19.1664C3.44193 19.1654 3.43959 19.1637 3.43729 19.1621L3.40869 19.1465L3.44424 19.1664Z" fill="#333333" />
              <path
                d="M14.702 9.22986V5.79543C14.7023 5.28909 14.6267 4.80398 14.498 4.36504C14.3689 3.9251 14.1882 3.52904 13.9737 3.17671C13.3327 2.13048 12.3922 1.37878 11.2633 0.839248L11.2572 0.836503C10.1228 0.302089 8.78605 0.000499218 7.35113 0C5.43734 0.0027457 3.70082 0.532167 2.37996 1.45023L2.37938 1.45073C1.71981 1.91276 1.15609 2.47444 0.72797 3.17715C0.513829 3.52904 0.333126 3.9251 0.204103 4.36504C0.0753525 4.80404 -0.000311536 5.28916 9.6421e-07 5.79549V14.2046C-0.000311536 14.7108 0.0753525 15.1955 0.204103 15.6345C0.333126 16.0745 0.513829 16.4705 0.728009 16.8228C1.36914 17.8691 2.30895 18.6208 3.43731 19.1598L3.44481 19.1635C4.57953 19.698 5.91598 19.9991 7.35117 20H20V9.22999H14.702V9.22986ZM1.47102 4.32766C1.91555 3.58894 2.71399 2.90139 3.73781 2.4214C4.76141 1.93859 6.00809 1.65223 7.35117 1.65266C9.14172 1.65129 10.7616 2.16311 11.9082 2.96504C12.4814 3.36436 12.9343 3.83656 13.2311 4.32766C13.5293 4.82245 13.6672 5.30981 13.6675 5.79543C13.6672 6.28048 13.5293 6.7684 13.2311 7.26319C12.7865 8.00141 11.9881 8.68896 10.9643 9.16902C9.94066 9.65226 8.69426 9.93819 7.35113 9.93819C5.56031 9.93912 3.94043 9.4273 2.79387 8.62581C2.22059 8.22649 1.7677 7.7543 1.47098 7.26319C1.17277 6.7684 1.03481 6.28048 1.03449 5.79543C1.03484 5.30987 1.17281 4.82245 1.47102 4.32766ZM13.6675 7.89957V9.93819H11.3929C11.6564 9.80078 11.9107 9.65413 12.1448 9.49089C12.768 9.05513 13.2833 8.53314 13.6559 7.91985L13.6675 7.89957ZM18.9655 18.3473H17.192V14.1754H16.6008V18.3473H15.2955V16.0877H14.7043V18.3473H13.3987V14.1754H12.8075V18.3473H11.502V16.0877H10.9108V18.3473H9.60551V14.1754H9.01434V18.3473H7.70906V16.0877H7.11789V18.339C6.66902 18.3279 6.23285 18.2855 5.81231 18.2135V14.1754H5.22113V18.0918C4.29254 17.8658 3.46121 17.5015 2.79387 17.0349C2.22059 16.6356 1.7677 16.1634 1.47098 15.6723C1.17277 15.1774 1.03481 14.6896 1.03449 14.2045V7.89957L1.04606 7.91985C1.6052 8.83754 2.48356 9.55959 3.57031 10.0747C4.65742 10.5875 5.9552 10.8821 7.35109 10.8826H18.9654V18.3473H18.9655Z"
                fill="#333333"
              />
            </svg>
          </button>
        </Tooltip>
        <Tooltip placement="bottom" title="Show Polygon Azimuth" mouseEnterDelay={0.25}>
          <button className={show_azi ? "button-active" : ""} style={{ padding: 0 }} disabled={false} onClick={() => handleToggleAzi()}>
            {/* <img draggable={false} src={protractor} /> */}
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clipPath="url(#clip0_427_191)">
                <path
                  d="M19.8 3.93697L16.8908 1.03168C16.6742 0.815156 16.4971 0.638004 16.1074 1.03168L14.887 2.25206C11.1865 -0.976048 5.88371 -0.629617 2.62018 2.63392C-0.741777 5.99193 -0.867752 11.3774 2.23831 14.8889L1.01793 16.1093C0.624263 16.503 0.915579 16.7903 1.01793 16.8927L3.92717 19.798C4.03739 19.9043 4.32084 20.1917 4.71057 19.798L19.8 4.72038C20.1937 4.3267 19.9023 4.03932 19.8 3.93697ZM17.7293 5.22034L16.9734 4.46449C16.7569 4.24797 16.4065 4.24797 16.19 4.46449C15.9735 4.68101 15.9735 5.03138 16.19 5.2479L16.9459 6.00374L16.0522 6.89738L15.6901 6.53914C15.4735 6.32262 15.1232 6.32262 14.9067 6.53914C14.6901 6.75566 14.6901 7.10602 14.9067 7.32254L15.2649 7.68078L14.3713 8.57048L13.6194 7.81463C13.4028 7.59811 13.0525 7.59811 12.8359 7.81463C12.6194 8.03115 12.6194 8.38152 12.8359 8.59804L13.5918 9.35389L12.6982 10.2436L12.3399 9.88534C12.1234 9.66882 11.773 9.66882 11.5565 9.88534C11.34 10.1019 11.34 10.4522 11.5565 10.6687L11.9148 11.027L11.0211 11.9206L10.2653 11.1648C10.0488 10.9483 9.69839 10.9483 9.48187 11.1648C9.26535 11.3813 9.26535 11.7317 9.48187 11.9482L10.2377 12.704L9.34408 13.5937L8.9819 13.2355C8.76539 13.019 8.41502 13.019 8.1985 13.2355C7.98198 13.452 7.98198 13.8024 8.1985 14.0189L8.56068 14.3771L7.66704 15.2668L6.91119 14.511C6.69467 14.2945 6.34431 14.2945 6.12779 14.511C5.91127 14.7275 5.91127 15.0779 6.12779 15.2944L6.88364 16.0502L5.99 16.9439L5.63176 16.5856C5.41524 16.3691 5.06488 16.3691 4.84836 16.5856C4.63184 16.8021 4.63184 17.1525 4.84836 17.369L5.2066 17.7273L4.3169 18.6209L2.19501 16.499L3.40358 15.2905C3.80513 14.8889 3.50594 14.6094 3.40358 14.507C0.344756 11.4482 0.344756 6.47221 3.40358 3.41339C6.46635 0.354561 11.4463 0.358498 14.5051 3.41339C14.7216 3.62991 14.9185 3.78344 15.2885 3.41339L16.4971 2.20482L18.619 4.3267L17.7293 5.22034Z"
                  fill="#333333"
                />
                <path
                  d="M12.5092 5.40532C10.5527 3.44483 7.20254 3.59837 5.39165 5.40532C3.42723 7.3658 3.42723 10.5585 5.39165 12.519C5.494 12.6213 5.78532 12.9126 6.17506 12.519L12.5092 6.18872C12.9029 5.79505 12.6116 5.50767 12.5092 5.40532ZM5.81288 11.3143C4.65942 9.77901 4.78146 7.58232 6.17899 6.18872C7.64739 4.72033 9.86769 4.70852 11.3085 5.82261L5.81288 11.3143Z"
                  fill="#333333"
                />
              </g>
              <defs>
                <clipPath id="clip0_427_191">
                  <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </button>
        </Tooltip>

        <Tooltip placement="bottom" title="Delete All" mouseEnterDelay={0.5}>
          <Popconfirm title="Are you sure?" onConfirm={deleteAll}>
            <button disabled={generated}>
              <img draggable={false} src={delete_polygon} />
            </button>
          </Popconfirm>
        </Tooltip>
        <MapboxExportMenu />
      </section>

      <section className={searchVisible || expandMapLayers || expandPolygonTools || expandExportTools ? "expand-down" : "map-action-tray"}>
        {searchVisible && <SearchLocations />}
        {expandMapLayers && <MapboxLayerMenu additional_layers={additional_layers_ref.current} />} {/* Pass updated additional_layers */}
        {selectedFeatureId && expandPolygonTools && !searchVisible && <PolygonTools id={selectedFeatureId} />}
      </section>
    </ToolbarWrap>
  );
};

export { MapboxToolbar };
